import { useEffect, useMemo } from 'react';
import { getMediaData, isVideo } from '../../ImageGalleryHelper';
import { getCarouselItemId, getIndicatorDotId } from '../ImageGalleryCarouselDesktop/utils';

/**
 * Custom hook that encapsulates all business logic for ImageGalleryCarousel
 * @param {Object} props - Component props
 * @param {Array} props.carouselImages - Array of carousel images
 * @param {Array} props.images - Array of all images
 * @param {number} props.activeImageIndex - Currently active image index
 * @param {number} props.activeIndicatorIndex - Currently active indicator index
 * @param {number} props.imagesPerSlide - Number of images per slide
 * @param {Function} props.setActiveImageIndex - Function to set active image index
 * @param {Function} props.setActiveIndicatorIndex - Function to set active indicator index
 * @param {string} props.productName - Product name for alt text
 * @param {string} props.brandName - Brand name for alt text
 * @param {number} props.carouselImageWidth - Width of carousel images
 * @param {number} props.carouselImageHeight - Height of carousel images
 * @returns {Object} - Processed data and handlers
 */
export const useImageGalleryCarousel = ({
  carouselImages = [],
  images = [],
  activeImageIndex,
  activeIndicatorIndex,
  imagesPerSlide,
  setActiveImageIndex,
  setActiveIndicatorIndex,
  productName,
  brandName,
  carouselImageWidth,
  carouselImageHeight,
  startIndex,
}) => {
  // Calculate if indicators should be shown
  const shouldShowIndicators = useMemo(() => {
    return Math.ceil(images.length / imagesPerSlide) > 1;
  }, [images.length, imagesPerSlide]);

  // Navigation logic for vertical carousel
  const maxVisibleItems = 6;

  // Determine which array to use for navigation logic
  // v1 uses carouselImages, v2 uses images with startIndex
  // v2 explicitly passes startIndex, v1 doesn't pass it at all
  const useV2Navigation = startIndex !== undefined;
  const sourceArray = useV2Navigation ? images : carouselImages;
  const actualStartIndex = startIndex || 0;

  const shouldShowNavigation = useMemo(() => {
    return sourceArray.length > maxVisibleItems;
  }, [sourceArray.length, maxVisibleItems]);

  // Calculate visible range based on startIndex
  const visibleCarouselItems = useMemo(() => {
    if (useV2Navigation) {
      // v2 logic: use images array with startIndex navigation
      if (!shouldShowNavigation) {
        return images.slice(0, maxVisibleItems);
      }
      return images.slice(actualStartIndex, actualStartIndex + maxVisibleItems);
    }
    // v1 logic: use carouselImages as-is (original behavior)
    return carouselImages;
  }, [useV2Navigation, images, carouselImages, actualStartIndex, shouldShowNavigation, maxVisibleItems]);

  // Navigation state
  const canScrollUp = useMemo(() => {
    if (!useV2Navigation) return false; // v1 doesn't use navigation
    return actualStartIndex > 0;
  }, [useV2Navigation, actualStartIndex]);

  const canScrollDown = useMemo(() => {
    if (!useV2Navigation) return false; // v1 doesn't use navigation
    return actualStartIndex + maxVisibleItems < images.length;
  }, [useV2Navigation, actualStartIndex, images.length, maxVisibleItems]);

  // Calculate total number of indicator dots
  const totalIndicators = useMemo(() => {
    return Math.ceil(images.length / imagesPerSlide);
  }, [images.length, imagesPerSlide]);

  // Generate indicator array for mapping
  const indicatorArray = useMemo(() => {
    return Array(totalIndicators).fill(null);
  }, [totalIndicators]);

  // Handle carousel item click
  const handleCarouselItemClick = useMemo(() => {
    return (index) => {
      if (useV2Navigation) {
        // v2 logic: index is relative to current page
        setActiveImageIndex(activeIndicatorIndex * imagesPerSlide + index);
      } else {
        // v1 logic: index is the actual image index
        setActiveImageIndex(index);
      }
    };
  }, [useV2Navigation, activeIndicatorIndex, imagesPerSlide, setActiveImageIndex]);

  // Handle indicator dot click
  const handleIndicatorClick = useMemo(() => {
    return (index) => {
      setActiveIndicatorIndex(index);
      setActiveImageIndex(index * imagesPerSlide);
    };
  }, [imagesPerSlide, setActiveImageIndex, setActiveIndicatorIndex]);

  // Synchronize activeIndicatorIndex when activeImageIndex changes (for v1 compatibility)
  useEffect(() => {
    if (!useV2Navigation && setActiveIndicatorIndex) {
      const expectedIndicatorIndex = Math.floor(activeImageIndex / imagesPerSlide);
      if (expectedIndicatorIndex !== activeIndicatorIndex) {
        setActiveIndicatorIndex(expectedIndicatorIndex);
      }
    }
  }, [useV2Navigation, activeImageIndex, activeIndicatorIndex, imagesPerSlide, setActiveIndicatorIndex]);

  // Generate carousel item data with processed information
  const processedCarouselItems = useMemo(() => {
    return visibleCarouselItems.map((image, visibleIndex) => {
      let actualIndex;
      if (useV2Navigation) {
        // v2 logic: calculate index based on startIndex + visibleIndex
        actualIndex = shouldShowNavigation ? actualStartIndex + visibleIndex : visibleIndex;
      } else {
        // v1 logic: calculate global index based on indicator page and visible position
        actualIndex = activeIndicatorIndex * imagesPerSlide + visibleIndex;
      }
      const { mediaId, mediaUrl } = getMediaData(image);
      const isActiveItem = activeImageIndex === actualIndex;
      const isVideoItem = isVideo(image);

      return {
        image,
        mediaId,
        mediaUrl,
        index: actualIndex,
        visibleIndex,
        isActive: isActiveItem,
        isVideo: isVideoItem,
        altText: `${brandName} - ${productName}`,
        id: getCarouselItemId(actualIndex),
        key: `${mediaId}-${actualIndex}`,
        width: carouselImageWidth,
        height: carouselImageHeight,
      };
    });
  }, [
    visibleCarouselItems,
    activeImageIndex,
    activeIndicatorIndex,
    imagesPerSlide,
    brandName,
    productName,
    carouselImageWidth,
    carouselImageHeight,
    actualStartIndex,
    shouldShowNavigation,
    useV2Navigation,
  ]);

  // Generate indicator data with processed information
  const processedIndicators = useMemo(() => {
    return indicatorArray.map((_, index) => ({
      index,
      isActive: index === activeIndicatorIndex,
      id: getIndicatorDotId(index),
      key: index,
    }));
  }, [indicatorArray, activeIndicatorIndex]);

  return {
    // Processed data
    processedCarouselItems,
    processedIndicators,

    // Computed values
    shouldShowIndicators,
    totalIndicators,
    shouldShowNavigation,
    canScrollUp,
    canScrollDown,

    // Event handlers
    handleCarouselItemClick,
    handleIndicatorClick,

    // Raw data (for backward compatibility)
    carouselImages,
    images,
    activeImageIndex,
    activeIndicatorIndex,
    imagesPerSlide,
  };
};

// Named export is already available above, removing default export to follow linting rules
